import { Button, DataCard, OverviewHeader } from '@brighterion/br-ui';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RoleService } from 'Services';
import { getEmptyStringReplacementText, handleOnClickEvent, handleKeyEvent } from 'Utils';
import EllipsisTooltip from 'Components/EllipsisTooltip';
import EditTenantGroupModal from '../EditTenantGroupModal';

import styles from './TenantGroupOverview.css';

type TenantGroupOverviewProps = {
    tenantGroupId: string;
    ruleData: GlobalRulesData;
    tenantGroup?: TenantGroup;
    tenantOverridesData?: TenantGroupOverrides;
    mode?: 'deployed' | 'revisionWindow';
};

const TenantGroupOverview = ({
    tenantGroupId,
    ruleData,
    tenantGroup,
    mode,
    tenantOverridesData
}: TenantGroupOverviewProps) => {
    const [numOverrides, numNewOverrides, numModifiedOverrides, activeOverrides] = React.useMemo(() => {
        let overridesTotalCount = 0;
        let newOverridesCount = 0;
        let modifiedOverridesCount = 0;
        let activeOverridesCount = 0;

        ruleData.rules.forEach((rule) => {
            if (rule.modifiedFlag === 'new') {
                newOverridesCount++;
            }
            if (rule.modifiedFlag === 'modified') {
                modifiedOverridesCount++;
            }
            if (rule.allowOverrides) {
                activeOverridesCount++;
            }
            overridesTotalCount++;
        });

        return [overridesTotalCount, newOverridesCount, modifiedOverridesCount, activeOverridesCount];
    }, [ruleData]);

    const [closeModal, setCloseModal] = useState(true);
    const { t } = useTranslation();
    const isRevisionWindow = mode === 'revisionWindow';
    const hasEditAccess = RoleService.hasTenantGroupWriteAccess && !isRevisionWindow && mode !== 'deployed';

    return (
        <>
            {hasEditAccess && (
                <EditTenantGroupModal
                    tenantGroupId={tenantGroupId}
                    tenantGroup={tenantGroup}
                    closeModal={closeModal}
                    setCloseModal={setCloseModal}
                />
            )}

            <OverviewHeader
                leftTitle={isRevisionWindow ? tenantGroup?.name : 'Tenant Group Overview'}
                rightTitle={
                    hasEditAccess && (
                        <Button
                            size='small'
                            variant='tertiary'
                            onClick={handleOnClickEvent(() => setCloseModal(false))}
                            onKeyDown={handleKeyEvent(() => setCloseModal(false))}
                        >
                            {t('Edit Tenant Group')}
                        </Button>
                    )
                }
                dataTestId='tenantGroup-overviewHeader'
            >
                <div className={styles.row}>
                    {mode === 'deployed' ? (
                        <>
                            <DataCard className={styles.overviewDataCard} label='Rules' data={numOverrides} />
                            <DataCard className={styles.overviewDataCard} label='Active' data={activeOverrides} />
                        </>
                    ) : (
                        <>
                            <DataCard
                                className={styles.overviewDataCard}
                                label='Tenants'
                                data={tenantGroup?.numberOfTenants}
                            />
                            <DataCard className={styles.overviewDataCard} label='Overrides' data={numOverrides} />
                            <DataCard className={styles.overviewDataCard} label='New' data={numNewOverrides} />
                            <DataCard
                                className={styles.overviewDataCard}
                                label='Modified'
                                data={numModifiedOverrides}
                            />
                        </>
                    )}

                    <DataCard
                        className={(styles.overviewDataCard, styles.lastOverviewDataCard)}
                        label='Description'
                        dataBoldFont
                        data={
                            <EllipsisTooltip
                                text={
                                    tenantOverridesData
                                        ? getEmptyStringReplacementText(
                                              tenantOverridesData?.config?.tenantGroupDescription
                                          )
                                        : getEmptyStringReplacementText(tenantGroup?.description)
                                }
                            />
                        }
                    />
                </div>
            </OverviewHeader>
        </>
    );
};

export default TenantGroupOverview;
